import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import moment from 'moment'

import { useAuth } from '../../../Hooks/UseAuth'
import { useProgrammation } from '../../../Hooks/UseProgrammation'
import { useProject } from '../../../Hooks/UseProject'
import useTranslation from '../../../Hooks/UseTranslation'

import { confirmOrRejectTask, updateTaskProgress } from '../../../Services/ProgrammationManagerService'
import { updateTaskProgressAction } from '../../../Redux/Actions/ProgrammationManagerActions'

import { Can } from '@/Casl/Casl'

import CheckIcon from '@/Components/SvgImages/Check'
import Spinner from '@/Components/SvgImages/Spinner/Spinner'
import TimesIcon from '@/Components/SvgImages/Times'

import './TaskProgress.scss'

const TaskProgress = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { ability } = useAuth()
  const { project } = useProject()
  const { itemOpened, setEditingItem, patchItemOpened } = useProgrammation()

  const [progress, setProgress] = useState(itemOpened.progress || 0)
  const [showMobileLocker, setShowMobileLocker] = useState(true)

  const [ confirming, setConfirming ] = useState(false)
  const [ denying, setDenying ] = useState(false)

  const containerRef = useRef<HTMLDivElement | null>(null)

  const handleSave = async () => {
    setEditingItem(true)
    const [error, data] = await updateTaskProgress(itemOpened?._id, progress)
    setEditingItem(false)

    if (error) {
      setShowMobileLocker(true)
      return false
    }

    const { task, parentTask, updatedTasks } = data

    patchItemOpened({
      progress: task.progress,
      startedAt: task.startedAt,
      endedAt: task.endedAt,
      executeDays: task.executeDays,
      confirmed: task.confirmed,
      confirmedAt: task.confirmedAt,
      comments: task.comments,
      status: task.status,
    })

    await dispatch(updateTaskProgressAction(task, parentTask, updatedTasks))
    setTimeout(() => setShowMobileLocker(true), 500)
  }

  const handleConfirmOrReject = async (action: 'confirm' | 'reject') => {

    if (action === 'confirm') {

      setConfirming(true)
    } else {

      setDenying(true)
    }

    const [error, data] = await confirmOrRejectTask(itemOpened?._id, action)

    setConfirming(false)
    setDenying(false)

    if (error) {

      setShowMobileLocker(true)
      return false
    }

    const { task, parentTask } = data

    patchItemOpened({
      progress: task.progress,
      confirmed: task.confirmed,
      confirmedAt: task.confirmedAt,
      comments: task.comments,
      status: task.status,
    })

    await dispatch(updateTaskProgressAction(task, parentTask))
    setTimeout(() => setShowMobileLocker(true), 500)
  }

  const [isDisabled, tapHelperMessage] = useMemo(() => {

    const hasTheProjectStarted = moment().isSameOrAfter(project.startsAt)
    if (!hasTheProjectStarted) return [true, t('PROGRAMMATION_MANAGER:ProjectHasNotStartedYet')]
    if (itemOpened.hasSubTasks) return [
      true,
      t('PROGRAMMATION_MANAGER:ProgressTapHelperItemProgressDependsOnItsWorks')
    ]

    const uncheckedRestrictions = itemOpened.restrictions?.filter((r: any) => !r.checked)

    if (uncheckedRestrictions.length > 0) return [
      true,
      t('PROGRAMMATION_MANAGER:ProgressTapHelperThereAreOpenConstraints')
    ]

    const isAdmin = ability.can('manage', 'all')
    if (isAdmin) return [false, '']

    if (itemOpened.confirmed) return [
      true,
      t('PROGRAMMATION_MANAGER:ProgressTapHelperItHasBeenConfirmed')
    ]

    if (itemOpened.endedAt) {
      const thisWeek = Number(moment().format('YYYYWW'))
      const taskEndedWeek = Number(moment(itemOpened.endedAt).format('YYYYWW'))
      if (thisWeek !== taskEndedWeek) return [
        true,
        t('PROGRAMMATION_MANAGER:ProgressTapHelperItCanNotBeUpdated')
      ]
    }

    return [false, '']
  }, [itemOpened])

  const handleShowMobileLocker = () => {

    if (itemOpened.hasSubTasks || isDisabled) return

    setShowMobileLocker(false)
  }

  useEffect(() => {
    setProgress(itemOpened.progress || 0)
  }, [itemOpened])

  // ✅ Click fuera para bloquear
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowMobileLocker(true)
      }
    }

    if (!showMobileLocker) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMobileLocker])

  return (
    <Form.Group controlId="formBasicRange" ref={containerRef}>
      <Form.Label>
        {itemOpened.hasSubTasks ? t('PROGRAMMATION_MANAGER:GeneralProgressLabel') : t('PROGRAMMATION_MANAGER:ProgressLabel')}
      </Form.Label>
      <div className="progress-confirm-container">
        <div className={`task-progress-container ${showMobileLocker ? '' : 'hide-locker'}`}>
          <div className="progress-number">
            {parseInt(progress)}%
          </div>
          <div className="progress-tap-helper">
            {tapHelperMessage}
          </div>
          <div
            className={`progress ${itemOpened.status} ${isDisabled ? 'disabled' : ''}`}
            style={{
              width: `calc(${progress}% + ${4 - (progress / 100) * 2}px)`
            }}
          >
            <div className="progress-number-white">
              {parseInt(progress)}%
            </div>
          </div>
          <Form.Control
            type="range"
            className={`task-progress ${itemOpened.status} ${isDisabled ? 'disabled' : ''}`}
            min={0}
            max={100}
            value={parseInt(progress)}
            onChange={(e: any) => setProgress(e.target.value)}
            onMouseUp={handleSave}
            onTouchEnd={handleSave}
            disabled={isDisabled}
          />
          <div
            className={`progress-mobile-locker`}
            onClick={() => handleShowMobileLocker()}
          />
        </div>
        {itemOpened.progress === 100 && !itemOpened.confirmed && (
          <Can I="confirm" a="task" ability={ability}>
            <div className="confirm-deny-buttons">
              <button
                className={`btn btn-${itemOpened.status}`}
                onClick={() => handleConfirmOrReject('confirm')}
                disabled={isDisabled || confirming || denying}
              >
                {confirming && (
                  <Spinner
                    height={24}
                    width={24}
                    fill={itemOpened.status === 'activa' ? '#34a854' : '#c04f14'}
                  />
                )}
                {!confirming && (
                  <CheckIcon
                    height={24}
                    width={24}
                    fill={itemOpened.status === 'activa' ? '#34a854' : '#c04f14'}
                  />
                )}
              </button>
              <Button
                variant="secondary"
                onClick={() => handleConfirmOrReject('reject')}
                disabled={isDisabled || confirming || denying}
              >
                {denying && (
                  <Spinner height={24} width={24} />
                )}
                {!denying && (
                  <TimesIcon height={24} width={24} />
                )}
              </Button>
            </div>
          </Can>
        )}
      </div>
    </Form.Group>
  )
}

export default TaskProgress